// import React from 'react';
import { KaryvaLogoAnimated3D } from '../KaryvaLogoAnimated3D';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Button } from '../ui/button';
import { useState } from 'react';

interface WebsiteLayoutProps {
  children: React.ReactNode;
  currentPage: string;
  onNavigate: (page: string) => void;
}

const verticalNavItems = [
  { id: 'for-construction', label: 'Construction' },
  { id: 'for-legal', label: 'Legal' },
  { id: 'for-healthcare', label: 'Healthcare' },
  { id: 'for-marketing', label: 'Marketing & Agency' },
];

export function WebsiteLayout({ children, currentPage, onNavigate }: WebsiteLayoutProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [industriesOpen, setIndustriesOpen] = useState(false);

  const navItems = [
    { id: 'home', label: 'Home' },
    { id: 'platform', label: 'Platform' },
    { id: 'solutions', label: 'Solutions' },
    { id: 'pricing', label: 'Pricing' },
    { id: 'about', label: 'About Us' },
    { id: 'careers', label: 'Careers' },
    { id: 'contact', label: 'Contact' },
  ];

  const isVerticalPage = currentPage.startsWith('for-');

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-[#C4CDD5]">
        <nav className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <button
              onClick={() => onNavigate('home')}
              className="flex items-center gap-3 group"
            >
              <div className="h-12 w-12">
                <KaryvaLogoAnimated3D size="large" darkBg={false} autoPlay={true} />
              </div>
            </button>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              {navItems.map((item) => {
                // Inject the Industries dropdown right after Solutions.
                if (item.id === 'solutions') {
                  return (
                    <div key="solutions-group" className="flex items-center gap-8">
                      <button
                        onClick={() => onNavigate(item.id)}
                        className={`text-sm transition-colors ${
                          currentPage === item.id
                            ? 'text-[#DAA520] font-medium'
                            : 'text-[#556B7C] hover:text-[#2A3B4C]'
                        }`}
                      >
                        {item.label}
                      </button>
                      <div
                        className="relative"
                        onMouseEnter={() => setIndustriesOpen(true)}
                        onMouseLeave={() => setIndustriesOpen(false)}
                      >
                        <button
                          onClick={() => setIndustriesOpen((o) => !o)}
                          className={`flex items-center gap-1 text-sm transition-colors ${
                            isVerticalPage
                              ? 'text-[#DAA520] font-medium'
                              : 'text-[#556B7C] hover:text-[#2A3B4C]'
                          }`}
                        >
                          Industries
                          <ChevronDown
                            className={`w-4 h-4 transition-transform ${industriesOpen ? 'rotate-180' : ''}`}
                          />
                        </button>
                        {industriesOpen && (
                          <div className="absolute top-full left-0 pt-2 z-50">
                            <div className="w-56 bg-white border border-[#C4CDD5] rounded-lg shadow-lg py-2">
                              {verticalNavItems.map((v) => (
                                <button
                                  key={v.id}
                                  onClick={() => {
                                    onNavigate(v.id);
                                    setIndustriesOpen(false);
                                  }}
                                  className={`block w-full text-left px-4 py-2 text-sm transition-colors ${
                                    currentPage === v.id
                                      ? 'bg-[#FFF9E6] text-[#DAA520] font-medium'
                                      : 'text-[#556B7C] hover:bg-[#F7F8F9] hover:text-[#2A3B4C]'
                                  }`}
                                >
                                  {v.label}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                }
                return (
                  <button
                    key={item.id}
                    onClick={() => onNavigate(item.id)}
                    className={`text-sm transition-colors ${
                      currentPage === item.id
                        ? 'text-[#DAA520] font-medium'
                        : 'text-[#556B7C] hover:text-[#2A3B4C]'
                    }`}
                  >
                    {item.label}
                  </button>
                );
              })}
              <Button
                onClick={() => onNavigate('contact')}
                className="bg-[#DAA520] hover:bg-[#B8860B] text-white"
              >
                Contact Sales
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden text-[#2A3B4C]"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="md:hidden mt-4 pb-4 space-y-3">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => {
                    onNavigate(item.id);
                    setMobileMenuOpen(false);
                  }}
                  className={`block w-full text-left px-4 py-2 rounded transition-colors ${
                    currentPage === item.id
                      ? 'bg-[#FFF9E6] text-[#DAA520] font-medium'
                      : 'text-[#556B7C] hover:bg-[#E8E9E9]'
                  }`}
                >
                  {item.label}
                </button>
              ))}
              <div className="pt-2 mt-2 border-t border-[#E8E9E9]">
                <div className="px-4 py-1 text-xs uppercase tracking-wider text-[#8B9BAB]">
                  Industries
                </div>
                {verticalNavItems.map((v) => (
                  <button
                    key={v.id}
                    onClick={() => {
                      onNavigate(v.id);
                      setMobileMenuOpen(false);
                    }}
                    className={`block w-full text-left px-4 py-2 rounded transition-colors ${
                      currentPage === v.id
                        ? 'bg-[#FFF9E6] text-[#DAA520] font-medium'
                        : 'text-[#556B7C] hover:bg-[#E8E9E9]'
                    }`}
                  >
                    {v.label}
                  </button>
                ))}
              </div>
              <Button
                onClick={() => {
                  onNavigate('contact');
                  setMobileMenuOpen(false);
                }}
                className="w-full bg-[#DAA520] hover:bg-[#B8860B] text-white"
              >
                Contact Sales
              </Button>
            </div>
          )}
        </nav>
      </header>

      {/* Main Content */}
      <main className="flex-1">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-[#2A3B4C] text-white">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            {/* Company Info */}
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10">
                  <KaryvaLogoAnimated3D size="header" darkBg={true} autoPlay={true} />
                </div>
              </div>
              <p className="text-[#A8B5C3] text-sm leading-relaxed mb-4">
                Enterprise Project Portfolio Management platform built on PMI/PMBOK principles.
                Delivering customized, secure, and scalable PPM solutions for organizations worldwide.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-medium mb-4">Platform</h4>
              <div className="space-y-2 text-sm text-[#A8B5C3]">
                <button onClick={() => onNavigate('platform')} className="block hover:text-[#DAA520] transition-colors">
                  Features
                </button>
                <button onClick={() => onNavigate('solutions')} className="block hover:text-[#DAA520] transition-colors">
                  Solutions
                </button>
                <button onClick={() => onNavigate('pricing')} className="block hover:text-[#DAA520] transition-colors">
                  Pricing
                </button>
                <button onClick={() => onNavigate('about')} className="block hover:text-[#DAA520] transition-colors">
                  About Us
                </button>
              </div>
            </div>

            {/* Company */}
            <div>
              <h4 className="font-medium mb-4">Company</h4>
              <div className="space-y-2 text-sm text-[#A8B5C3]">
                <button onClick={() => onNavigate('careers')} className="block hover:text-[#DAA520] transition-colors">
                  Careers
                </button>
                <button onClick={() => onNavigate('contact')} className="block hover:text-[#DAA520] transition-colors">
                  Contact
                </button>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-[#3E5365] pt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-[#A8B5C3]">
            <p>© {new Date().getFullYear()} Karyva. All rights reserved.</p>
            <div className="flex gap-6">
              <button onClick={() => onNavigate('privacy')} className="hover:text-[#DAA520] transition-colors">Privacy Policy</button>
              <button onClick={() => onNavigate('terms')} className="hover:text-[#DAA520] transition-colors">Terms of Service</button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}